import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = '/api/demo/platform';

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  bg:      '#0f172a', bgCard:  '#1e293b', bgSide:  '#131c2e',
  border:  '#334155', primary: '#2563eb', accent:  '#7c3aed',
  success: '#10b981', warn:    '#f59e0b', danger:  '#ef4444',
  text:    '#f1f5f9', muted:   '#94a3b8', dim:     '#475569',
  positive:'#10b981', neutral: '#f59e0b', negative:'#ef4444',
};

const S = {
  shell: { display:'flex', height:'100vh', background:C.bg, color:C.text, fontFamily:'Inter,system-ui,sans-serif', overflow:'hidden' } as React.CSSProperties,
  sidebar: { width:220, background:C.bgSide, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column' as const, flexShrink:0 },
  main: { flex:1, display:'flex', flexDirection:'column' as const, overflow:'hidden' },
  header: { height:52, background:C.bgCard, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', padding:'0 20px', gap:12, flexShrink:0 },
  content: { flex:1, overflow:'auto', padding:20 },
  card: { background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:10, padding:16 },
  kpiGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12 },
  badge: (color: string): React.CSSProperties => ({ background: color + '22', color, borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700 }),
};

// ─── Nav icons (SVG inline) ────────────���──────────────────────────────────────
const Icon = {
  dash:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  wall:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="5"/><rect x="3" y="11" width="9" height="10"/><rect x="15" y="11" width="6" height="10"/></svg>,
  cdr:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  reports: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  phone:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-2.68-7.85A2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  ai:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M8 12h.01M12 12h.01M16 12h.01" strokeWidth="2.5"/></svg>,
  crm:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  demo:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
};

// ─── Helpers ────────────────────────────────────────────────���─────────────────
function fmt(s: number) { return `${Math.floor(s/60)}m ${s%60}s`; }
function fmtDate(d: string) { return new Date(d).toLocaleString(); }
function sentimentColor(s: string) { return s === 'positive' ? C.positive : s === 'negative' ? C.danger : C.warn; }
function sentimentEmoji(s: string) { return s === 'positive' ? '😊' : s === 'negative' ? '😞' : '😐'; }

// ─── Mini bar chart (pure CSS) ─────────��──────────────────────────────────────
function MiniBarChart({ data, color = C.primary }: { data: { label: string; value: number }[]; color?: string }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:50 }}>
      {data.map((d, i) => (
        <div key={i} title={`${d.label}: ${d.value}`}
          style={{ flex:1, background: color + '88', borderRadius:'2px 2px 0 0',
                   height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? 3 : 0,
                   transition:'height 0.4s ease' }} />
      ))}
    </div>
  );
}

// ─── KPI Card ──────────────���────────────────────────────────────��─────────────
function KpiCard({ label, value, sub, color = C.primary, icon }: any) {
  return (
    <div style={{ ...S.card, textAlign:'center', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:10, right:12, fontSize:22, opacity:0.15 }}>{icon}</div>
      <div style={{ fontSize:28, fontWeight:800, color, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:C.dim, marginTop:2 }}>{sub}</div>}
    </div>
  );
}

// ��── Agent state dot ──────────────────────────────────────────────────────────
const STATE_COLOR: Record<string, string> = { available: C.success, on_call: C.danger, break: C.warn, wrap_up: C.accent, offline: C.dim };
const STATE_LABEL: Record<string, string> = { available: 'Available', on_call: 'On Call', break: 'Break', wrap_up: 'Wrap-up', offline: 'Offline' };

// ─── Disposition badge ──────────────────���─────────────────────────────────────
const DISP_COLOR: Record<string, string> = { ANSWERED: C.success, 'NO ANSWER': C.warn, BUSY: C.dim, VOICEMAIL: C.accent };

// ─── Dashboard Tab ───────────────────���────────────────────────────────────────
function DashboardTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    fetch(`${API}/${sessionId}/dashboard`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(setData).catch(() => {});
  }, [sessionId, token]);

  if (!data) return <div style={{ color: C.muted, padding:40, textAlign:'center' }}>Loading dashboard…</div>;

  const kpis = [
    { label: 'Total Calls Today',  value: data.total_calls,       color: C.primary,  icon: '📞' },
    { label: 'Answered',           value: data.answered,          color: C.success,  icon: '✅' },
    { label: 'Abandoned',          value: data.abandoned,         color: C.warn,     icon: '⚠️' },
    { label: 'Service Level',      value: `${data.service_level}%`, color: data.service_level >= 85 ? C.success : C.warn, icon: '📊' },
    { label: 'Avg Handle Time',    value: data.aht_formatted,     color: C.accent,   icon: '⏱️' },
    { label: 'Agents Available',   value: data.agents_available,  color: C.success,  icon: '🧑‍💼' },
    { label: 'Agents On Call',     value: data.agents_on_call,    color: C.danger,   icon: '📱' },
    { label: 'Calls Waiting',      value: data.calls_in_queue,    color: data.calls_in_queue > 3 ? C.danger : C.muted, icon: '⏳' },
  ];

  const trend7 = data.trend_30d?.slice(-7) || [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>📊 {data.company}</div>
        <span style={S.badge(C.accent)}>{data.industry || 'Demo'}</span>
        <span style={{ marginLeft:'auto', ...S.badge(C.success) }}>🟢 Live</span>
      </div>

      <div style={S.kpiGrid}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Call Volume Trend */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>📈 Calls — Last 7 Days</div>
          <MiniBarChart data={trend7.map((d: any) => ({ label: d.date, value: d.calls }))} color={C.primary} />
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
            {trend7.map((d: any, i: number) => (
              <div key={i} style={{ fontSize:9, color:C.dim }}>{d.date.slice(5)}</div>
            ))}
          </div>
        </div>

        {/* Queue Stats */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>📋 Queue Performance</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {(data.queue_stats || []).slice(0,4).map((q: any, i: number) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                <div style={{ flex:1, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{q.name}</div>
                <div style={{ width:120, height:6, background:C.bg, borderRadius:3 }}>
                  <div style={{ width:`${q.service_level}%`, height:'100%', background: q.service_level>=85 ? C.success : C.warn, borderRadius:3 }} />
                </div>
                <div style={{ color: q.service_level>=85 ? C.success : C.warn, fontSize:11, fontWeight:600, width:36, textAlign:'right' }}>{q.service_level}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Occupancy */}
      <div style={{ ...S.card }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
          <div style={{ fontSize:13, fontWeight:600 }}>🏆 Agent Occupancy</div>
          <div style={{ fontSize:12, color:C.muted }}>Target: ≥70%</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ fontSize:36, fontWeight:800, color: data.occupancy >= 70 ? C.success : C.warn }}>{data.occupancy}%</div>
          <div style={{ flex:1 }}>
            <div style={{ height:10, background:C.bg, borderRadius:5 }}>
              <div style={{ width:`${data.occupancy}%`, height:'100%', background: data.occupancy>=70 ? C.success : C.warn, borderRadius:5, transition:'width 1s ease' }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.dim, marginTop:4 }}>
              <span>0%</span><span>70% target</span><span>100%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Wallboard Tab ───────────��──────────────────────────────���─────────────────
function WallboardTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    const load = () =>
      fetch(`${API}/${sessionId}/wallboard`, { headers: { 'X-Demo-Token': token } })
        .then(r => r.json()).then(setData).catch(() => {});
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [sessionId, token]);

  if (!data) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Loading wallboard…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>📡 Live Wallboard</div>
        <span style={S.badge(C.success)}>🟢 LIVE</span>
        <span style={{ marginLeft:'auto', fontSize:11, color:C.dim }}>Auto-refresh every 8s</span>
      </div>

      {/* Top KPIs */}
      <div style={S.kpiGrid}>
        <KpiCard label="Calls Waiting"  value={data.calls_waiting}    color={data.calls_waiting > 3 ? C.danger : C.success} icon="⏳" />
        <KpiCard label="Active Calls"   value={data.calls_active}     color={C.primary} icon="📞" />
        <KpiCard label="Available"      value={data.agents_available} color={C.success} icon="✅" />
        <KpiCard label="Service Level"  value={`${data.service_level}%`} color={data.service_level>=85 ? C.success : C.warn} icon="📊" />
        <KpiCard label="Avg Talk Time"  value={fmt(data.avg_talk_time)}  color={C.accent}  icon="⏱️" />
        <KpiCard label="Occupancy"      value={`${data.occupancy}%`}    color={C.muted}   icon="📈" />
      </div>

      {/* Agent Grid */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>👥 Agent Status</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:8 }}>
          {(data.agents || []).map((a: any, i: number) => (
            <div key={i} style={{ background:C.bg, borderRadius:8, padding:10, border:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:10, height:10, borderRadius:'50%', background: STATE_COLOR[a.state] || C.dim, flexShrink:0, boxShadow: `0 0 6px ${STATE_COLOR[a.state] || C.dim}` }} />
              <div style={{ overflow:'hidden' }}>
                <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{a.name}</div>
                <div style={{ fontSize:11, color: STATE_COLOR[a.state] || C.dim }}>{STATE_LABEL[a.state] || a.state}</div>
                {a.state === 'on_call' && <div style={{ fontSize:10, color:C.dim }}>{fmt(a.call_duration)}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Longest Wait */}
      <div style={{ ...S.card, display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:11, color:C.muted, flexShrink:0 }}>LONGEST WAIT</div>
        <div style={{ fontSize:24, fontWeight:800, color: data.longest_wait_secs > 60 ? C.danger : C.success }}>{fmt(data.longest_wait_secs)}</div>
        <div style={{ flex:1 }} />
        <div style={{ fontSize:11, color:C.dim }}>Updated {new Date().toLocaleTimeString()}</div>
      </div>
    </div>
  );
}

// ─── CDR Tab ───────────────────────────────────────────────────��──────────────
function CdrTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    fetch(`${API}/${sessionId}/cdr?page=${page}&limit=15`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(setData).catch(() => {});
  }, [sessionId, token, page]);

  if (!data) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Loading call history…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>📋 Call History</div>
        <span style={S.badge(C.muted)}>{data.total} records</span>
        <span style={{ marginLeft:'auto', ...S.badge(C.accent) }}>Synthetic Demo Data</span>
      </div>
      <div style={S.card}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}` }}>
              {['Time','Direction','Caller','Duration','Disposition','Queue','Agent','Sentiment'].map(h => (
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:C.muted, fontWeight:600, fontSize:11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.records || []).map((r: any, i: number) => (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}22`, transition:'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = C.bg)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <td style={{ padding:'6px 10px', color:C.dim }}>{new Date(r.call_time).toLocaleString()}</td>
                <td style={{ padding:'6px 10px' }}><span style={S.badge(r.direction==='inbound' ? C.primary : C.accent)}>{r.direction}</span></td>
                <td style={{ padding:'6px 10px', fontFamily:'monospace', color:C.muted }}>{r.caller}</td>
                <td style={{ padding:'6px 10px', color: r.duration_seconds > 0 ? C.text : C.dim }}>{r.duration_seconds > 0 ? fmt(r.duration_seconds) : '—'}</td>
                <td style={{ padding:'6px 10px' }}><span style={S.badge(DISP_COLOR[r.disposition] || C.dim)}>{r.disposition}</span></td>
                <td style={{ padding:'6px 10px', color:C.muted, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.queue_name}</td>
                <td style={{ padding:'6px 10px', color:C.text }}>{r.agent_name}</td>
                <td style={{ padding:'6px 10px' }}>{r.sentiment ? `${sentimentEmoji(r.sentiment)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:12 }}>
          <button disabled={page===1} onClick={() => setPage(p=>p-1)}
            style={{ padding:'4px 12px', background:C.bg, color:C.text, border:`1px solid ${C.border}`, borderRadius:6, cursor:'pointer', opacity: page===1 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ color:C.muted, fontSize:12, alignSelf:'center' }}>Page {page}</span>
          <button disabled={(page * 15) >= data.total} onClick={() => setPage(p=>p+1)}
            style={{ padding:'4px 12px', background:C.bg, color:C.text, border:`1px solid ${C.border}`, borderRadius:6, cursor:'pointer', opacity: (page*15)>=data.total ? 0.4 : 1 }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// ─── Reports Tab ──────────���──────────────────────────────��────────────────────
function ReportsTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    fetch(`${API}/${sessionId}/reports`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(setData).catch(() => {});
  }, [sessionId, token]);

  if (!data) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Loading reports…</div>;

  const peak = (data.hourly_distribution || []).reduce((p: any, h: any) => h.calls > (p?.calls || 0) ? h : p, null);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:18, fontWeight:700 }}>📈 Analytics & Reports</div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Hourly heatmap */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>☀️ Call Volume by Hour</div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:60 }}>
            {(data.hourly_distribution || []).map((h: any, i: number) => {
              const max = Math.max(...(data.hourly_distribution||[]).map((x: any) => x.calls), 1);
              return (
                <div key={i} title={`${h.hour}:00 — ${h.calls} calls`}
                  style={{ flex:1, background: i===peak?.hour ? C.warn : C.primary + '88', borderRadius:'2px 2px 0 0',
                           height:`${(h.calls/max)*100}%`, minHeight: h.calls>0?2:0 }} />
              );
            })}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:9, color:C.dim, marginTop:4 }}>
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
          </div>
          {peak && <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>Peak: <strong style={{ color:C.warn }}>{peak.hour}:00</strong> ({peak.calls} calls)</div>}
        </div>

        {/* Sentiment */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>😊 Sentiment Analysis</div>
          {data.sentiment && (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[['positive', C.success, '😊'], ['neutral', C.warn, '😐'], ['negative', C.danger, '😞']].map(([k, col, em]) => {
                const val = (data.sentiment as any)[k as string] || 0;
                const total = (data.sentiment as any).total || 1;
                return (
                  <div key={k as string} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                    <span style={{ width:20 }}>{em as string}</span>
                    <div style={{ flex:1, height:8, background:C.bg, borderRadius:4 }}>
                      <div style={{ width:`${(val/total)*100}%`, height:'100%', background: col as string, borderRadius:4 }} />
                    </div>
                    <span style={{ color: col as string, fontWeight:600, width:32, textAlign:'right' }}>{Math.round((val/total)*100)}%</span>
                  </div>
                );
              })}
              <div style={{ fontSize:11, color:C.dim }}>Based on {data.sentiment.total} calls</div>
            </div>
          )}
        </div>
      </div>

      {/* Agent Leaderboard */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>🏆 Agent Leaderboard</div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}` }}>
              {['#','Agent','Calls Handled','Avg Handle Time','Satisfaction','Occupancy'].map(h => (
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:C.muted, fontWeight:600, fontSize:11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.leaderboard || []).slice(0, 8).map((a: any, i: number) => (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}22` }}>
                <td style={{ padding:'6px 10px', color: i < 3 ? C.warn : C.dim, fontWeight:700 }}>{i+1}</td>
                <td style={{ padding:'6px 10px', fontWeight:500 }}>{a.name}</td>
                <td style={{ padding:'6px 10px', color:C.primary, fontWeight:700 }}>{a.calls_handled}</td>
                <td style={{ padding:'6px 10px', color:C.muted }}>{fmt(a.avg_handle_time)}</td>
                <td style={{ padding:'6px 10px' }}>{'⭐'.repeat(Math.round(parseFloat(a.satisfaction)))} {a.satisfaction}</td>
                <td style={{ padding:'6px 10px' }}>
                  <span style={{ color: a.occupancy >= 70 ? C.success : C.warn }}>{a.occupancy}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Queue SLA */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>🎯 Queue SLA Summary</div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {(data.queue_sla || []).map((q: any, i: number) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, fontSize:12 }}>
              <div style={{ width:150, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{q.queue}</div>
              <div style={{ flex:1, height:8, background:C.bg, borderRadius:4 }}>
                <div style={{ width:`${q.sla_percent}%`, height:'100%', background: q.sla_percent>=85 ? C.success : C.warn, borderRadius:4 }} />
              </div>
              <span style={{ color: q.sla_percent>=85 ? C.success : C.warn, fontWeight:700, width:36 }}>{q.sla_percent}%</span>
              <span style={{ color:C.dim, width:70 }}>AHT: {fmt(q.aht)}</span>
              <span style={{ color: parseFloat(q.abandoned_pct) > 5 ? C.danger : C.dim, width:60 }}>Abn: {q.abandoned_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Softphone Tab ────────────────────────────────────────────────────���───────
function SoftphoneTab({ sessionId, token, botName }: { sessionId: string; token: string; botName: string }) {
  const [creds, setCreds]   = useState<any>(null);
  const [callState, setCallState] = useState<'idle'|'calling'|'connected'|'ended'>('idle');
  const [callTimer, setCallTimer] = useState(0);
  const [dialInput, setDialInput] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval>|null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/${sessionId}/sip-account`, {
      method: 'POST', headers: { 'X-Demo-Token': token, 'Content-Type': 'application/json' }
    }).then(r => r.json()).then(d => { setCreds(d); setLoading(false); }).catch(() => setLoading(false));
  }, [sessionId, token]);

  const startSimCall = (dest: string) => {
    setDialInput(dest);
    setCallState('calling');
    setCallTimer(0);
    setTimeout(() => {
      setCallState('connected');
      timerRef.current = setInterval(() => setCallTimer(t => {
        if (t >= 29) { hangup(); return t; }
        return t + 1;
      }), 1000);
    }, 2000);
  };

  const hangup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCallState('ended');
    setTimeout(() => { setCallState('idle'); setCallTimer(0); }, 2000);
  };

  const dialPadKeys = ['1','2','3','4','5','6','7','8','9','*','0','#'];
  const callLimitSeconds = creds?.call_limit_seconds || 30;
  const clampedPct = Math.min((callTimer / callLimitSeconds) * 100, 100);
  const timeRemaining = Math.max(0, callLimitSeconds - callTimer);

  const routing = creds?.outbound_routing || [
    { pattern: '+971x (UAE)', trunk: 'tec-sbc-1', cli: creds?.cli_uae || '+97180004000' },
    { pattern: '04x / 05x (UAE local)', trunk: 'tec-sbc-1', cli: creds?.cli_uae || '+97180004000' },
    { pattern: '+44x (UK)', trunk: 'esbc', cli: creds?.cli_uk || '+44800000000' },
    { pattern: '+x (International)', trunk: 'esbc', cli: creds?.cli_uk || '+44800000000' },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>📱 Browser Softphone</div>
        <span style={{ ...S.badge(C.danger), fontSize:12, fontWeight:700 }}>⏱ {callLimitSeconds}s Demo Limit</span>
      </div>

      {/* 30-second limit notice */}
      <div style={{ background:'#7c3aed22', border:`1px solid ${C.accent}`, borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:12 }}>
        <div style={{ fontSize:22, flexShrink:0 }}>⏱</div>
        <div>
          <div style={{ fontWeight:700, color:C.accent, fontSize:13 }}>30-Second Demo Call Limit</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
            All PSTN outbound calls are limited to <strong style={{color:C.text}}>30 seconds of connected time</strong> in the demo environment.
            Calls auto-disconnect when the limit is reached. This limit is enforced at the Asterisk dialplan level.
          </div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* SIP Credentials + Registration */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>🔐 SIP Registration</div>
          {loading ? (
            <div style={{ color:C.muted }}>Provisioning SIP account…</div>
          ) : creds ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8, fontSize:12 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background: C.success, boxShadow:`0 0 6px ${C.success}` }} />
                <span style={{ color:C.success, fontWeight:600 }}>Registered</span>
                <span style={{ color:C.dim, marginLeft:'auto' }}>WSS/WebRTC</span>
              </div>
              <div><span style={{ color:C.muted }}>Username: </span><code style={{ color:C.text }}>{creds.username}</code></div>
              <div><span style={{ color:C.muted }}>Domain: </span><code style={{ color:C.text }}>{creds.sip_domain}</code></div>
              <div><span style={{ color:C.muted }}>Transport: </span><code style={{ color:C.text }}>WSS (Secure WebSocket)</code></div>

              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, marginTop:4 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginBottom:4 }}>📞 OUTBOUND CALLER ID</div>
                <div style={{ display:'flex', gap:8, marginBottom:2 }}>
                  <span style={{ ...S.badge('#10b981'), fontSize:10 }}>🇦🇪 UAE</span>
                  <code style={{ color:C.text, fontSize:12 }}>{creds.cli_uae || '+97180004000'}</code>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <span style={{ ...S.badge(C.primary), fontSize:10 }}>🇬🇧 UK/Intl</span>
                  <code style={{ color:C.text, fontSize:12 }}>{creds.cli_uk || '+44800000000'}</code>
                </div>
                <div style={{ fontSize:10, color:C.dim, marginTop:4 }}>Toll-free numbers presented to callee. Exact numbers subject to availability.</div>
              </div>

              <div style={{ background:C.bg, padding:8, borderRadius:6, marginTop:4 }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>⚡ Demo Extensions</div>
                <div>🤖 <strong>7777</strong> — AI Voice Bot ({botName})</div>
                <div>🔊 <strong>7778</strong> — Echo Test</div>
                <div>📋 <strong>7779</strong> — Welcome IVR</div>
              </div>
            </div>
          ) : (
            <div style={{ color:C.warn }}>SIP provisioning unavailable in this demo environment</div>
          )}
        </div>

        {/* Dial pad */}
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>📲 Dial Pad</div>
          <input value={dialInput} onChange={e => setDialInput(e.target.value)} placeholder="Enter number or extension…"
            style={{ width:'100%', padding:'8px 12px', background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, fontSize:14, boxSizing:'border-box', marginBottom:10 }} />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:12 }}>
            {dialPadKeys.map(k => (
              <button key={k} onClick={() => setDialInput(d => d + k)}
                style={{ padding:'10px 0', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:16, fontWeight:600, cursor:'pointer' }}>{k}</button>
            ))}
          </div>

          {callState === 'idle' && (
            <button onClick={() => startSimCall(dialInput || '7777')} disabled={!dialInput && !creds}
              style={{ width:'100%', padding:'10px 0', background:C.success, border:'none', borderRadius:8, color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer' }}>📞 Call</button>
          )}
          {callState === 'calling' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ color:C.warn, marginBottom:8 }}>🔔 Calling {dialInput}…</div>
              <button onClick={hangup} style={{ padding:'8px 20px', background:C.danger, border:'none', borderRadius:8, color:'#fff', cursor:'pointer' }}>End Call</button>
            </div>
          )}
          {callState === 'connected' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ color:C.success, fontWeight:700, marginBottom:4 }}>✅ Connected</div>
              <div style={{ fontFamily:'monospace', fontSize:22, marginBottom:4, color: timeRemaining <= 10 ? C.danger : C.text }}>
                {`${String(Math.floor(callTimer/60)).padStart(2,'0')}:${String(callTimer%60).padStart(2,'0')}`}
              </div>
              {/* 30s progress bar */}
              <div style={{ height:6, background:C.bg, borderRadius:3, marginBottom:6 }}>
                <div style={{ width:`${clampedPct}%`, height:'100%', background: timeRemaining <= 10 ? C.danger : C.success, borderRadius:3, transition:'width 1s linear' }} />
              </div>
              <div style={{ fontSize:11, color: timeRemaining <= 10 ? C.danger : C.dim, marginBottom:8 }}>
                {timeRemaining <= 10 ? `⚠️ ${timeRemaining}s remaining` : `${timeRemaining}s of 30s remaining`}
              </div>
              <button onClick={hangup} style={{ padding:'8px 20px', background:C.danger, border:'none', borderRadius:8, color:'#fff', cursor:'pointer' }}>📵 Hang Up</button>
            </div>
          )}
          {callState === 'ended' && <div style={{ textAlign:'center', color:C.muted }}>Call ended</div>}
        </div>
      </div>

      {/* Outbound routing table */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>🔀 Outbound Trunk Routing</div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}` }}>
              {['Destination Pattern','Trunk','Carrier Route','Outbound CLI'].map(h => (
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:C.muted, fontSize:11, fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {routing.map((r: any, i: number) => (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}22` }}>
                <td style={{ padding:'6px 10px', fontFamily:'monospace', color:C.text }}>{r.pattern}</td>
                <td style={{ padding:'6px 10px' }}>
                  <span style={{ ...S.badge(r.trunk === 'tec-sbc-1' ? '#10b981' : C.accent), fontSize:11 }}>{r.trunk}</span>
                </td>
                <td style={{ padding:'6px 10px', color:C.muted }}>
                  {r.trunk === 'tec-sbc-1' ? 'tec-sbc-1.synergytalk.org:5061' : 'esbc.synergytalk.org:5061'}
                </td>
                <td style={{ padding:'6px 10px', fontFamily:'monospace', color:C.primary }}>{r.cli}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop:8, fontSize:11, color:C.dim }}>
          All routes enforce <strong style={{ color:C.warn }}>30s connected-time limit</strong> via Asterisk L(30000) dialplan option.
          UAE numbers use dedicated tec-sbc-1 trunk; all international routes via esbc production trunk.
        </div>
      </div>

      {/* Quick call buttons */}
      <div style={S.card}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>⚡ Quick Dial</div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {[
            { label: `🤖 Call ${botName} AI`, ext:'7777', color:C.primary },
            { label: '🔊 Echo Test',          ext:'7778', color:C.success },
            { label: '📋 Demo IVR',            ext:'7779', color:C.accent  },
          ].map(b => (
            <button key={b.ext} onClick={() => startSimCall(b.ext)} disabled={callState !== 'idle'}
              style={{ padding:'8px 16px', background: b.color + '22', border:`1px solid ${b.color}`, borderRadius:8, color: b.color, fontWeight:600, cursor:'pointer', opacity: callState !== 'idle' ? 0.5 : 1 }}>
              {b.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize:11, color:C.dim, marginTop:10 }}>
          ⚠️ Softphone UI is simulated in this demo. In production, the browser registers via WebRTC to Asterisk (WSS transport).
          PSTN calls connect through tec-sbc-1 (UAE) or esbc (International) trunks with toll-free CLI presentation.
        </div>
      </div>
    </div>
  );
}

function AiAssistTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData]   = useState<any>(null);
  const [scene, setScene] = useState<any>(null);
  useEffect(() => {
    fetch(`${API}/${sessionId}/ai-assist`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(d => { setData(d); if (d.scenarios?.length) setScene(d.scenarios[0]); }).catch(() => {});
  }, [sessionId, token]);

  if (!data) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Loading AI Assist…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:18, fontWeight:700 }}>🤖 AI Assist — Live Call Panel</div>
      {scene && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {/* Live transcript */}
          <div style={S.card}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>📝 Live Transcript</div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:C.danger, animation:'pulse 1.5s infinite' }} />
              <span style={{ fontSize:11, color:C.danger }}>LIVE</span>
              <span style={{ fontSize:11, color:C.muted, marginLeft:'auto' }}>Caller: {scene.caller}</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {(scene.transcript || []).map((t: any, i: number) => (
                <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                  <div style={{ fontSize:11, color: t.speaker === 'agent' ? C.primary : C.muted, fontWeight:600, flexShrink:0, paddingTop:2 }}>
                    {t.speaker === 'agent' ? '🧑 Agent' : '👤 Customer'}
                  </div>
                  <div style={{ background:C.bg, borderRadius:8, padding:'6px 10px', fontSize:12, flex:1 }}>{t.text}</div>
                </div>
              ))}
            </div>
            {/* Sentiment */}
            <div style={{ marginTop:12, padding:10, background:C.bg, borderRadius:8, display:'flex', alignItems:'center', gap:10 }}>
              <div>Sentiment: <strong style={{ color: sentimentColor(scene.sentiment) }}>{sentimentEmoji(scene.sentiment)} {scene.sentiment}</strong></div>
              <div style={{ marginLeft:'auto', fontSize:11, color:C.muted }}>Score: {scene.sentiment_score?.toFixed(2)}</div>
            </div>
          </div>

          {/* AI Suggestions */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {/* Suggested replies */}
            <div style={S.card}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>💬 Suggested Replies</div>
              {(scene.suggested_replies || []).map((r: string, i: number) => (
                <div key={i} style={{ background:C.bg, borderRadius:6, padding:'8px 10px', fontSize:12, marginBottom:6, cursor:'pointer', border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.primary}` }}>
                  {r}
                </div>
              ))}
            </div>

            {/* Knowledge */}
            <div style={S.card}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>📚 Knowledge Articles</div>
              {(scene.knowledge_articles || []).map((a: any, i: number) => (
                <div key={i} style={{ background:C.bg, borderRadius:6, padding:'8px 10px', marginBottom:6 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.primary }}>{a.title}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{a.snippet}</div>
                </div>
              ))}
            </div>

            {/* Compliance + actions */}
            <div style={S.card}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>✅ Compliance & Actions</div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <span style={{ fontSize:12, color:C.muted }}>Compliance Score:</span>
                <span style={{ fontWeight:700, color: scene.compliance_score >= 90 ? C.success : C.warn }}>{scene.compliance_score}%</span>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:C.muted, marginBottom:4 }}>Action Items</div>
              {(scene.action_items || []).map((a: string, i: number) => (
                <div key={i} style={{ fontSize:12, color:C.text, padding:'3px 0' }}>◻ {a}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Customer summary */}
      {scene?.customer_summary && (
        <div style={{ ...S.card, borderLeft:`3px solid ${C.accent}` }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>👤 Customer Intelligence</div>
          <div style={{ fontSize:12, color:C.muted }}>{scene.customer_summary}</div>
        </div>
      )}
    </div>
  );
}

// ─── CRM Tab ────────────────────���────────────────────────────��────────────────
function CrmTab({ sessionId, token }: { sessionId: string; token: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    fetch(`${API}/${sessionId}/crm`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(setData).catch(() => {});
  }, [sessionId, token]);

  if (!data) return <div style={{ color:C.muted, padding:40, textAlign:'center' }}>Loading CRM…</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>👥 CRM Contacts</div>
        <span style={S.badge(C.accent)}>Synthetic Demo Data</span>
      </div>
      <div style={S.card}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}` }}>
              {['Name','Company','Phone','Email','Status','Calls','Last Contact','Sentiment'].map(h => (
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:C.muted, fontSize:11, fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.contacts || []).map((c: any, i: number) => (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}22` }}>
                <td style={{ padding:'6px 10px', fontWeight:600 }}>{c.contact_name}</td>
                <td style={{ padding:'6px 10px', color:C.muted }}>{c.company}</td>
                <td style={{ padding:'6px 10px', fontFamily:'monospace', color:C.dim }}>{c.phone}</td>
                <td style={{ padding:'6px 10px', color:C.muted, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis' }}>{c.email}</td>
                <td style={{ padding:'6px 10px' }}><span style={S.badge(c.status==='VIP' ? C.warn : c.status==='customer' ? C.success : C.muted)}>{c.status}</span></td>
                <td style={{ padding:'6px 10px', color:C.primary, fontWeight:600 }}>{c.total_calls}</td>
                <td style={{ padding:'6px 10px', color:C.dim }}>{c.last_call_date ? new Date(c.last_call_date).toLocaleDateString() : '—'}</td>
                <td style={{ padding:'6px 10px' }}>{sentimentEmoji(c.sentiment)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Shell ───────────────────────���───────────────────────────────────────
interface Props {
  sessionId: string;
  botName:   string;
  botGreeting: string;
  demoNumber: string | null;
  onBack?:   () => void;
}

export default function DemoPlatformDashboard({ sessionId, botName, botGreeting, demoNumber, onBack }: Props) {
  const [token, setToken]    = useState<string | null>(null);
  const [error, setError]    = useState<string | null>(null);
  const [session, setSession] = useState<any>(null);
  const [tab, setTab]        = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');

  // Issue platform token on mount
  useEffect(() => {
    fetch(`/api/demo/session/${sessionId}/platform-token`, { method:'POST' })
      .then(r => r.json())
      .then(d => {
        if (d.platform_token) {
          setToken(d.platform_token);
          // Countdown timer
          const exp = new Date(d.expires_at).getTime();
          const tick = () => {
            const rem = Math.max(0, exp - Date.now());
            const h = Math.floor(rem/3600000), m = Math.floor((rem%3600000)/60000);
            setTimeLeft(`${h}h ${m}m`);
          };
          tick();
          setInterval(tick, 60000);
        } else {
          setError(d.error || 'Failed to activate demo');
        }
        setLoading(false);
      })
      .catch(() => { setError('Network error activating demo platform'); setLoading(false); });
  }, [sessionId]);

  // Load session details
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/${sessionId}/session`, { headers: { 'X-Demo-Token': token } })
      .then(r => r.json()).then(setSession).catch(() => {});
  }, [sessionId, token]);

  if (loading) return (
    <div style={{ ...S.shell, justifyContent:'center', alignItems:'center', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:36 }}>⚡</div>
      <div style={{ fontSize:18, fontWeight:700 }}>Activating Demo Platform…</div>
      <div style={{ color:C.muted }}>Generating synthetic data for your industry…</div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.shell, justifyContent:'center', alignItems:'center', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:36 }}>⚠️</div>
      <div style={{ fontSize:18, fontWeight:700, color:C.danger }}>{error}</div>
      <button onClick={onBack} style={{ padding:'8px 20px', background:C.primary, border:'none', borderRadius:8, color:'#fff', cursor:'pointer' }}>← Back</button>
    </div>
  );

  const TABS = [
    { id:'dashboard', label:'Dashboard',   icon: Icon.dash    },
    { id:'wallboard', label:'Wallboard',   icon: Icon.wall    },
    { id:'cdr',       label:'Call History',icon: Icon.cdr     },
    { id:'reports',   label:'Reports',     icon: Icon.reports },
    { id:'softphone', label:'Softphone',   icon: Icon.phone   },
    { id:'ai',        label:'AI Assist',   icon: Icon.ai      },
    { id:'crm',       label:'CRM',         icon: Icon.crm     },
  ];

  return (
    <div style={S.shell}>
      {/* Sidebar */}
      <div style={S.sidebar}>
        {/* Logo */}
        <div style={{ padding:'16px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ background:`linear-gradient(135deg,${C.primary},${C.accent})`, borderRadius:8, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>📞</div>
          <div>
            <div style={{ fontSize:13, fontWeight:800 }}>CallEssential</div>
            <div style={{ fontSize:10, color:C.muted }}>Demo Platform</div>
          </div>
        </div>

        {/* Company badge */}
        {session && (
          <div style={{ margin:'10px 12px', background:C.bg, borderRadius:8, padding:'8px 10px' }}>
            <div style={{ fontSize:12, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{session.lead_company || 'Demo Company'}</div>
            <div style={{ fontSize:10, color:C.muted }}>{session.lead_industry || 'Demo'} · {session.lead_name}</div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex:1, padding:'8px 0', overflowY:'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background: tab === t.id ? C.primary + '22' : 'transparent', border:'none', color: tab===t.id ? C.primary : C.muted, cursor:'pointer', fontSize:13, fontWeight: tab===t.id ? 600 : 400, borderLeft: `2px solid ${tab===t.id ? C.primary : 'transparent'}` }}>
              {t.icon}<span>{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding:'12px 14px', borderTop:`1px solid ${C.border}` }}>
          {demoNumber && <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>📞 {demoNumber}</div>}
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:C.warn, display:'inline-block' }} />
            <span style={{ fontSize:11, color:C.warn }}>Demo — expires {timeLeft}</span>
          </div>
          {onBack && (
            <button onClick={onBack} style={{ marginTop:8, width:'100%', padding:'5px 0', background:'transparent', border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, cursor:'pointer', fontSize:11 }}>
              ← Back to Setup
            </button>
          )}
        </div>
      </div>

      {/* Main area */}
      <div style={S.main}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ fontSize:14, fontWeight:700 }}>
            {TABS.find(t=>t.id===tab)?.icon} {TABS.find(t=>t.id===tab)?.label}
          </div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
            <span style={S.badge(C.accent)}>🎭 Demo Mode</span>
            <span style={S.badge(C.success)}>🟢 Live</span>
            {demoNumber && <span style={{ fontSize:12, color:C.muted }}>Bot: {demoNumber}</span>}
          </div>
        </div>

        {/* Content */}
        <div style={S.content}>
          {token && (
            <>
              {tab === 'dashboard' && <DashboardTab  sessionId={sessionId} token={token} />}
              {tab === 'wallboard' && <WallboardTab  sessionId={sessionId} token={token} />}
              {tab === 'cdr'       && <CdrTab        sessionId={sessionId} token={token} />}
              {tab === 'reports'   && <ReportsTab    sessionId={sessionId} token={token} />}
              {tab === 'softphone' && <SoftphoneTab  sessionId={sessionId} token={token} botName={botName} />}
              {tab === 'ai'        && <AiAssistTab   sessionId={sessionId} token={token} />}
              {tab === 'crm'       && <CrmTab        sessionId={sessionId} token={token} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
